import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CPFInput, CNPJInput } from '@/components/ui/cpf-cnpj-input';
import { maskPhone, isValidCPF, isValidCNPJ, onlyDigits } from '@/lib/formatters';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useCanWrite } from '../hooks/useCanWrite';
import { useCnpjLookup } from '../hooks/useCnpjLookup';

const EMPTY_FORM_DATA = {
  tipo: 'PF',
  // PF
  nome: '',
  sexo: 'Masculino',
  cpf: '',
  profissao: '',
  // PJ
  nome_fantasia: '',
  razao_social: '',
  cnpj: '',
  inscricao_municipal: '',
  inscricao_estadual: '',
  ramo_atuacao: '',
  site: '',
  // Contato Financeiro PJ
  contato_financeiro_nome: '',
  contato_financeiro_whatsapp: '',
  contato_financeiro_email: '',
  // Endereço
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  ponto_referencia: '',
  cep: '',
  cidade: '',
  estado: '',
  // Contato
  telefone_fixo: '',
  whatsapp: '',
  email: ''
};

// Mesma tela de cadastro/edição de Cliente usada em pages/Clientes.jsx, extraída para reuso
// (ex: cadastro rápido a partir de Contas a Receber quando o cliente ainda não existe).
export const ClienteFormDialog = ({ open, onOpenChange, cliente, companyId, onSaved, initialNome }) => {
  const [tipoCliente, setTipoCliente] = useState('PF');
  const [cpfValido, setCpfValido] = useState(true);
  const [cnpjValido, setCnpjValido] = useState(true);
  const { checkCanWrite } = useCanWrite();
  const { buscarCNPJ, loadingCNPJ } = useCnpjLookup();
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  useEffect(() => {
    if (!open) return;
    if (cliente) {
      setTipoCliente(cliente.tipo);
      setFormData({ ...EMPTY_FORM_DATA, ...cliente });
    } else {
      setTipoCliente('PF');
      setFormData({ ...EMPTY_FORM_DATA, nome: initialNome || '' });
    }
    setCpfValido(true);
    setCnpjValido(true);
  }, [open, cliente, initialNome]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checkCanWrite(cliente ? 'editar cliente' : 'criar cliente')) return;

    if (tipoCliente === 'PF') {
      if (formData.cpf && !isValidCPF(formData.cpf)) {
        toast.error('CPF inválido. Por favor, corrija antes de continuar.');
        setCpfValido(false);
        return;
      }
      if (!formData.cpf || onlyDigits(formData.cpf).length < 11) {
        toast.error('CPF é obrigatório para Pessoa Física.');
        setCpfValido(false);
        return;
      }
    }

    if (tipoCliente === 'PJ') {
      if (formData.cnpj && !isValidCNPJ(formData.cnpj)) {
        toast.error('CNPJ inválido. Por favor, corrija antes de continuar.');
        setCnpjValido(false);
        return;
      }
      if (!formData.cnpj || onlyDigits(formData.cnpj).length < 14) {
        toast.error('CNPJ é obrigatório para Pessoa Jurídica.');
        setCnpjValido(false);
        return;
      }
    }

    try {
      const data = {
        ...formData,
        tipo: tipoCliente,
        empresa_id: companyId
      };

      let saved;
      if (cliente) {
        await axiosInstance.put(`/clientes/${cliente.id}`, data);
        saved = { ...data, id: cliente.id };
        toast.success('Cliente atualizado com sucesso!');
      } else {
        const res = await axiosInstance.post('/clientes', data);
        saved = res.data.cliente;
        toast.success('Cliente cadastrado com sucesso!');
      }

      onOpenChange(false);
      onSaved?.(saved);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl">
            {cliente ? 'Editar Cliente' : 'Novo Cliente'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Toggle Tipo */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => setTipoCliente('PF')}
              className={tipoCliente === 'PF' ? 'bg-blue-600' : 'bg-gray-600'}
            >
              Pessoa Física
            </Button>
            <Button
              type="button"
              onClick={() => setTipoCliente('PJ')}
              className={tipoCliente === 'PJ' ? 'bg-green-600' : 'bg-gray-600'}
            >
              Pessoa Jurídica
            </Button>
          </div>

          {tipoCliente === 'PF' ? (
            /* PESSOA FÍSICA */
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Nome Completo *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    required
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Sexo</Label>
                  <select
                    value={formData.sexo}
                    onChange={(e) => setFormData({...formData, sexo: e.target.value})}
                    className="w-full p-2 glass border-white/10 text-white rounded-md"
                  >
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">CPF *</Label>
                  <CPFInput
                    value={formData.cpf}
                    onChange={(value) => {
                      setFormData({...formData, cpf: value});
                      setCpfValido(true); // Reset validação ao digitar
                    }}
                    onValidation={(isValid) => setCpfValido(isValid)}
                    required
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Profissão</Label>
                  <Input
                    value={formData.profissao}
                    onChange={(e) => setFormData({...formData, profissao: e.target.value})}
                    className="glass border-white/10 text-white"
                  />
                </div>
              </div>
            </>
          ) : (
            /* PESSOA JURÍDICA */
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Nome Fantasia *</Label>
                  <Input
                    value={formData.nome_fantasia}
                    onChange={(e) => setFormData({...formData, nome_fantasia: e.target.value})}
                    required
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Razão Social *</Label>
                  <Input
                    value={formData.razao_social}
                    onChange={(e) => setFormData({...formData, razao_social: e.target.value})}
                    required
                    className="glass border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white flex items-center gap-2">
                    CNPJ *
                    {loadingCNPJ && <Loader2 size={14} className="animate-spin text-gray-400" />}
                  </Label>
                  <CNPJInput
                    value={formData.cnpj}
                    onChange={async (value) => {
                      setFormData(prev => ({...prev, cnpj: value}));
                      setCnpjValido(true);
                      const digits = onlyDigits(value);
                      if (digits.length === 14) {
                        const dados = await buscarCNPJ(digits);
                        if (dados) {
                          setFormData(prev => ({
                            ...prev,
                            razao_social: dados.razao_social || prev.razao_social,
                            nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
                            cep: dados.cep || prev.cep,
                            logradouro: dados.logradouro || prev.logradouro,
                            numero: dados.numero || prev.numero,
                            complemento: dados.complemento || prev.complemento,
                            bairro: dados.bairro || prev.bairro,
                            cidade: dados.municipio || prev.cidade,
                            estado: dados.uf || prev.estado,
                            telefone_fixo: dados.telefone || prev.telefone_fixo,
                            email: dados.email || prev.email,
                            ramo_atuacao: dados.cnae_fiscal_descricao || prev.ramo_atuacao,
                          }));
                        }
                      }
                    }}
                    onValidation={(isValid) => setCnpjValido(isValid)}
                    required
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Inscrição Municipal</Label>
                  <Input
                    value={formData.inscricao_municipal}
                    onChange={(e) => setFormData({...formData, inscricao_municipal: e.target.value})}
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Inscrição Estadual</Label>
                  <Input
                    value={formData.inscricao_estadual}
                    onChange={(e) => setFormData({...formData, inscricao_estadual: e.target.value})}
                    className="glass border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Ramo de Atuação</Label>
                  <Input
                    value={formData.ramo_atuacao}
                    onChange={(e) => setFormData({...formData, ramo_atuacao: e.target.value})}
                    className="glass border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Site/URL</Label>
                  <Input
                    value={formData.site}
                    onChange={(e) => setFormData({...formData, site: e.target.value})}
                    placeholder="https://"
                    className="glass border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Contato Financeiro */}
              <div className="border-t border-white/10 pt-4">
                <h3 className="text-white font-bold mb-3">Contato Financeiro</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white">Nome</Label>
                    <Input
                      value={formData.contato_financeiro_nome}
                      onChange={(e) => setFormData({...formData, contato_financeiro_nome: e.target.value})}
                      className="glass border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">WhatsApp</Label>
                    <Input
                      value={formData.contato_financeiro_whatsapp}
                      onChange={(e) => setFormData({...formData, contato_financeiro_whatsapp: maskPhone(e.target.value)})}
                      placeholder="(00) 00000-0000"
                      className="glass border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">E-mail</Label>
                    <Input
                      type="email"
                      value={formData.contato_financeiro_email}
                      onChange={(e) => setFormData({...formData, contato_financeiro_email: e.target.value})}
                      className="glass border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Endereço (comum) */}
          <div className="border-t border-white/10 pt-4">
            <h3 className="text-white font-bold mb-3">Endereço</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-3">
                <Label className="text-white">Logradouro</Label>
                <Input
                  value={formData.logradouro}
                  onChange={(e) => setFormData({...formData, logradouro: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Número</Label>
                <Input
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <Label className="text-white">{tipoCliente === 'PF' ? 'Complemento (AP/Casa)' : 'Complemento (Sala)'}</Label>
                <Input
                  value={formData.complemento}
                  onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Bairro</Label>
                <Input
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">CEP</Label>
                <Input
                  value={formData.cep}
                  onChange={(e) => setFormData({...formData, cep: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <Label className="text-white">Cidade</Label>
                <Input
                  value={formData.cidade}
                  onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Estado (UF)</Label>
                <Input
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                  maxLength={2}
                  placeholder="SP"
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Ponto de Referência</Label>
                <Input
                  value={formData.ponto_referencia}
                  onChange={(e) => setFormData({...formData, ponto_referencia: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Contato (comum) */}
          <div className="border-t border-white/10 pt-4">
            <h3 className="text-white font-bold mb-3">Contato</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-white">Telefone Fixo</Label>
                <Input
                  value={formData.telefone_fixo}
                  onChange={(e) => setFormData({...formData, telefone_fixo: maskPhone(e.target.value)})}
                  placeholder="(00) 0000-0000"
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">WhatsApp</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({...formData, whatsapp: maskPhone(e.target.value)})}
                  placeholder="(00) 00000-0000"
                  className="glass border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">E-mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="glass border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-white/10"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {cliente ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClienteFormDialog;
